import { MouseEvent, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import FileSelect from "datasets/pages/tables/components/FileSelect";
import FileValidationModal from "datasets/pages/tables/components/file-validation/FileValidationModal";
import { useIngestionHydration } from "datasets/pages/tables/components/file-validation/hooks/useIngestionHydration";
import { ValidationMetadata } from "datasets/pages/tables/components/file-validation/types";
import { stripS3Prefix } from "datasets/pages/tables/components/file-validation/utils/jobParsers";
import useIngestContext from "datasets/pages/tables/contexts/ingest";
import useActiveIngests from "datasets/pages/tables/hooks/useActiveIngests";
import useDestinationSchemas from "datasets/pages/tables/hooks/useDestinationSchemas";
import useFileMetadata from "datasets/pages/tables/hooks/useFileMetadata";
import { Ingest } from "datasets/pages/tables/types/interfaces/ingest";
import { normalizeColumnNames } from "datasets/pages/tables/utils/columns";
import { resolveRouterPath } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import { ChevronDown, FileText, Search, Settings, Upload, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";
import { apiMutate, isApiError, parseApiErrorBody } from "~/utils/apiClient";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const DEFAULT_NULL_IF = ["\\N", "NULL", ""];

export const IngestForm = () => {
  const navigate = useNavigate();
  const { datasetId } = useParams<{ datasetId: string }>();
  const { showError } = useToast();
  const { mutate: mutateActiveIngests } = useActiveIngests();
  const {
    dataProfile,
    setDataProfile,
    errors,
    setErrors,
    isFileConfirmed,
    setIsFileConfirmed,
    tableExists,
  } = useIngestContext();

  const { schemas, isLoading: isLoadingSchemas } = useDestinationSchemas();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fileConfigOpen, setFileConfigOpen] = useState(false);
  const [openSection, setOpenSection] = useState("section-1");
  const [nullIfInput, setNullIfInput] = useState("");
  const [schemaPickerOpen, setSchemaPickerOpen] = useState(false);
  const [schemaSearch, setSchemaSearch] = useState("");
  const [showDescriptions, setShowDescriptions] = useState(false);

  type ConfigForm = {
    metadata_platform_storage_key: string;
    delimiter: string;
    encoding: string;
    null_if: string[];
  };
  const configForm = useForm<ConfigForm>({
    defaultValues: {
      metadata_platform_storage_key: "",
      delimiter: ",",
      encoding: "UTF8",
      null_if: [],
    },
  });
  const [metadataPopperAnchor, setMetadataPopperAnchor] = useState<HTMLElement | null>(null);
  const [pendingMetadataFile, setPendingMetadataFile] = useState<FileInfo | null>(null);
  const [selectedMetadataFileKey, setSelectedMetadataFileKey] = useState<string | undefined>(
    undefined
  );

  const {
    data: selectedFileMetadata,
    error: selectedFileError,
    isLoading: isLoadingMetadataFile,
  } = useFileMetadata(selectedMetadataFileKey);

  const { hydratedData } = useIngestionHydration();
  const [searchParams] = useSearchParams();
  const currentIngestionId = searchParams.get("ingestion");

  const form = useForm<Ingest>({
    mode: "onChange",
    defaultValues: {
      file_size: 0,
      platform_storage_key: "",
      metadata_platform_storage_key: "",
      metadata_config: {
        columns: [],
        database_schema: "",
        table_description: "",
        table: "",
        tags: [],
      },
    },
  });

  const prevIngestionIdRef = useRef<string | null>(currentIngestionId);
  useEffect(() => {
    if (
      prevIngestionIdRef.current !== null &&
      currentIngestionId !== null &&
      prevIngestionIdRef.current !== currentIngestionId
    ) {
      form.reset();
      setDataProfile(null);
      setErrors([]);
      setIsFileConfirmed(false);
      hasHydrated.current = false;
    }
    prevIngestionIdRef.current = currentIngestionId;
  }, [currentIngestionId, form, setDataProfile, setErrors, setIsFileConfirmed]);

  const hasHydrated = useRef(false);
  useEffect(() => {
    if (hydratedData && !hasHydrated.current) {
      hasHydrated.current = true;
      if (hydratedData.sourceFileUri) {
        form.setValue("source_file_uri", hydratedData.sourceFileUri);
      }
      if (hydratedData.platformStorageKey) {
        form.setValue("platform_storage_key", hydratedData.platformStorageKey);
      }
      if (hydratedData.fileFormat) {
        form.setValue("metadata_config.file_format", hydratedData.fileFormat);
      }
      if (hydratedData.dataProfile?.columns) {
        const profileColumns = hydratedData.dataProfile.columns.map((col) => ({
          name: col.name,
          type: col.type,
          description: "",
          tags: [],
        }));
        form.setValue("metadata_config.columns", profileColumns);
      }
      if (hydratedData.dataProfile) {
        setDataProfile(hydratedData.dataProfile);
      }
      setIsFileConfirmed(true);
    }
  }, [hydratedData, form, setDataProfile, setIsFileConfirmed]);

  useEffect(() => {
    if (hydratedData?.fileSize) {
      form.setValue("file_size", hydratedData.fileSize);
    }
  }, [hydratedData?.fileSize, form]);

  useEffect(() => {
    if (fileConfigOpen) {
      configForm.reset({
        metadata_platform_storage_key: form.getValues("metadata_platform_storage_key") ?? "",
        delimiter: form.getValues("metadata_config.file_format.delimiter") ?? ",",
        encoding: form.getValues("metadata_config.file_format.encoding") ?? "UTF8",
        null_if: form.getValues("metadata_config.file_format.null_if") ?? DEFAULT_NULL_IF,
      });
      setNullIfInput("");
      setSelectedMetadataFileKey(undefined);
      setPendingMetadataFile(null);
    }
  }, [fileConfigOpen]);

  useEffect(() => {
    if (!selectedMetadataFileKey) {
      return;
    }
    if (selectedFileMetadata) {
      configForm.setValue("metadata_platform_storage_key", selectedMetadataFileKey, {
        shouldDirty: true,
      });
      setMetadataPopperAnchor(null);
    } else if (selectedFileError) {
      setMetadataPopperAnchor(null);
    }
  }, [selectedFileMetadata, selectedFileError, selectedMetadataFileKey]);

  const handleFileValidated = (file: FileInfo, meta: ValidationMetadata) => {
    if (meta.validatedFilePath) {
      form.setValue(
        "platform_storage_key",
        stripS3Prefix(meta.validatedFilePath) ?? meta.validatedFilePath
      );
    }
    if (meta.file_format) {
      form.setValue("metadata_config.file_format", meta.file_format);
    }
    if (meta.dataProfile) {
      setDataProfile(meta.dataProfile);
      const profileColumns = meta.dataProfile.columns.map((col) => ({
        name: col.name,
        type: col.type,
        description: "",
        tags: [],
      }));
      form.setValue("metadata_config.columns", profileColumns);
    }
    setIsFileConfirmed(true);
    setOpenSection("section-2");
  };

  const storageKey = form.watch("platform_storage_key");
  const schema = form.watch("metadata_config.database_schema");
  const tableName = form.watch("metadata_config.table");
  const columns = form.watch("metadata_config.columns");

  // When the dataset offers exactly one destination there is nothing to choose:
  // select it automatically and disable the control below.
  const singleSchemaName = schemas.length === 1 ? schemas[0].name : null;

  useEffect(() => {
    if (singleSchemaName && schema !== singleSchemaName) {
      form.setValue("metadata_config.database_schema", singleSchemaName);
    }
  }, [singleSchemaName, schema, form]);

  const schemaDisplayValue = schema || singleSchemaName || "";
  const schemaSearchLower = schemaSearch.toLowerCase();
  const filteredSchemas = schemas.filter(
    (s) =>
      s.name.toLowerCase().includes(schemaSearchLower) ||
      (showDescriptions && s.description.toLowerCase().includes(schemaSearchLower))
  );

  const section2Enabled = Boolean(storageKey && isFileConfirmed && schema);
  const section3Enabled = Boolean(section2Enabled && schema && tableName && columns?.length > 0);

  const doSubmit = async (data: Ingest) => {
    setErrors([]);
    try {
      const body = {
        dataset_id: datasetId,
        ...(data.source_file_uri
          ? { source_file_uri: data.source_file_uri }
          : { platform_storage_key: data.platform_storage_key }),
        metadata_config: {
          ...data.metadata_config,
          columns: normalizeColumnNames(data.metadata_config.columns),
        },
      };

      await apiMutate(`${VITE_CATALOG_API}/ingests`, "POST", body);

      trackEvent("dataset_table_ingest_submitted", {
        dataset_id: datasetId,
        table: data.metadata_config.table,
        database_schema: data.metadata_config.database_schema,
        columns: data.metadata_config.columns.length,
      });

      mutateActiveIngests();
      navigate(`/nova/datasets/${datasetId}/tables`);
    } catch (e: any) {
      if (isApiError(e)) {
        const detail = parseApiErrorBody(e)?.detail;
        if (detail !== undefined) {
          setErrors(Array.isArray(detail) ? detail : [detail]);
          return;
        }
      }
      rumClient?.recordError(e);
      showError("There was a problem attempting to ingest the data.");
    }
  };

  const onSubmit = (data: Ingest) => {
    if (tableExists) {
      setConfirmOpen(true);
    } else {
      return doSubmit(data);
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3">
        {errors.length > 0 && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
            {errors.map((err, i) => (
              <p key={i} className="text-sm text-destructive">
                {err}
              </p>
            ))}
          </div>
        )}

        <Accordion type="single" collapsible value={openSection} onValueChange={setOpenSection}>
          {/* Section 1: Data Import */}
          <AccordionItem value="section-1">
            <AccordionTrigger className="text-sm font-medium">1. Data Import</AccordionTrigger>
            <AccordionContent className="flex flex-col gap-3 pt-2">
              <div className="flex flex-col gap-1.5">
                <Label>Source File</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setWizardOpen(true)}
                    className="w-fit max-w-sm justify-start truncate"
                  >
                    <Upload className="mr-2 size-4 shrink-0" />
                    <span className="truncate">
                      {isFileConfirmed && storageKey
                        ? resolveRouterPath(storageKey)
                        : currentIngestionId
                          ? "Continue validation..."
                          : "Select file"}
                    </span>
                  </Button>
                  {isFileConfirmed && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 text-muted-foreground hover:text-foreground"
                      onClick={() => setFileConfigOpen(true)}
                      aria-label="Edit file configuration"
                    >
                      <Settings className="size-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Destination</Label>
                <Popover
                  open={schemaPickerOpen}
                  onOpenChange={(open) => {
                    setSchemaPickerOpen(open);
                    if (!open) setSchemaSearch("");
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isLoadingSchemas || Boolean(singleSchemaName)}
                      className="w-full justify-between"
                    >
                      <span
                        className={cn(
                          "truncate text-left font-normal",
                          !schemaDisplayValue && "text-muted-foreground"
                        )}
                      >
                        {schemaDisplayValue || "Select destination..."}
                      </span>
                      <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                  >
                    <div className="border-b p-2">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          autoFocus
                          placeholder="Search..."
                          value={schemaSearch}
                          onChange={(e) => setSchemaSearch(e.target.value)}
                          className="pl-7 h-8 text-sm"
                        />
                      </div>
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {filteredSchemas.map((s) => (
                        <button
                          key={s.name}
                          type="button"
                          className={cn(
                            "w-full px-3 py-2 text-left text-sm hover:bg-muted",
                            schema === s.name && "bg-muted font-medium"
                          )}
                          onClick={() => {
                            form.setValue("metadata_config.database_schema", s.name);
                            setSchemaPickerOpen(false);
                            setSchemaSearch("");
                          }}
                        >
                          <span className="block truncate">{s.name}</span>
                          {showDescriptions && s.description && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {s.description}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
                      <p className="text-xs text-muted-foreground">
                        Imported data will be stored as a new table within the selected destination.
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowDescriptions((prev) => !prev)}
                        className="shrink-0 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                      >
                        {showDescriptions ? "Hide descriptions" : "Show descriptions"}
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit"
                disabled={!section2Enabled}
                onClick={() => setOpenSection("section-2")}
              >
                Next
              </Button>
            </AccordionContent>
          </AccordionItem>

          {/* Section 2: Table Configuration */}
          <AccordionItem value="section-2" disabled={!section2Enabled}>
            <AccordionTrigger className="text-sm font-medium" disabled={!section2Enabled}>
              2. Table Configuration
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 pt-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="table">Table Name</Label>
                <Input
                  id="table"
                  placeholder="e.g. patients"
                  {...form.register("metadata_config.table", { required: true })}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="description">Table Description (optional)</Label>
                <Textarea
                  id="description"
                  rows={3}
                  placeholder="Describe this table..."
                  {...form.register("metadata_config.table_description")}
                />
              </div>

              {dataProfile && (
                <div className="flex flex-col gap-1.5">
                  <p className="text-sm font-medium">Columns ({dataProfile.columns.length})</p>
                  <div className="rounded-md border">
                    {dataProfile.columns.map((col, i) => (
                      <div
                        key={col.name}
                        className="flex items-center justify-between border-b px-3 py-2 text-sm last:border-0"
                      >
                        <span className="font-mono">{col.name}</span>
                        <span className="text-xs text-muted-foreground">{col.type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {section2Enabled && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-fit"
                  disabled={!schema || !tableName}
                  onClick={() => setOpenSection("section-3")}
                >
                  Next
                </Button>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Section 3: Review */}
          <AccordionItem value="section-3" disabled={!section3Enabled}>
            <AccordionTrigger className="text-sm font-medium" disabled={!section3Enabled}>
              3. Review
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 pt-2">
              <div className="rounded-md border p-4">
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex gap-3">
                    <span className="w-24 text-muted-foreground">Schema</span>
                    <span className="font-medium">{schema}</span>
                  </div>
                  <div className="flex gap-3">
                    <span className="w-24 text-muted-foreground">Table</span>
                    <span className="font-medium">{tableName}</span>
                  </div>
                  <div className="flex gap-3">
                    <span className="w-24 text-muted-foreground">Columns</span>
                    <span className="font-medium">{columns?.length ?? 0}</span>
                  </div>
                  {storageKey && (
                    <div className="flex items-center gap-3">
                      <span className="w-24 text-muted-foreground">File</span>
                      <span className="flex items-center gap-1 font-medium">
                        <FileText className="size-3.5" />
                        {storageKey}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                disabled={!section3Enabled || form.formState.isSubmitting}
                className="w-fit"
              >
                {form.formState.isSubmitting ? "Importing…" : "Import Table"}
              </Button>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>

      <FileValidationModal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onFileValidated={handleFileValidated}
        onConfirm={() => setWizardOpen(false)}
      />

      <Dialog open={fileConfigOpen} onOpenChange={setFileConfigOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit Source File Configuration</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div className="relative flex flex-col gap-1.5">
              <Label>Metadata File</Label>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between"
                disabled={isLoadingMetadataFile}
                onClick={(e: MouseEvent<HTMLButtonElement>) =>
                  setMetadataPopperAnchor(e.currentTarget)
                }
              >
                <span
                  className={`truncate text-left font-normal ${configForm.watch("metadata_platform_storage_key") ? "" : "text-muted-foreground"}`}
                >
                  {configForm.watch("metadata_platform_storage_key")
                    ? resolveRouterPath(configForm.watch("metadata_platform_storage_key"))
                    : "Select metadata file..."}
                </span>
                <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
              <FileSelect
                popperAnchorEl={metadataPopperAnchor}
                handlePopperClose={() => setMetadataPopperAnchor(null)}
                selectedFile={pendingMetadataFile}
                setSelectedFile={setPendingMetadataFile}
                onSelectFile={() => {
                  if (pendingMetadataFile) {
                    setSelectedMetadataFileKey(pendingMetadataFile.key);
                  }
                }}
                loading={isLoadingMetadataFile}
                maxHeight={280}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Delimiter</Label>
              <Select
                value={configForm.watch("delimiter") ?? ","}
                onValueChange={(v) => configForm.setValue("delimiter", v, { shouldDirty: true })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select delimiter" />
                </SelectTrigger>
                <SelectContent className="z-[1300]">
                  <SelectItem value=",">Comma</SelectItem>
                  <SelectItem value="\t">Tab</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Null If</Label>
              <Input
                placeholder="Add null value..."
                value={nullIfInput}
                onChange={(e) => setNullIfInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") {
                    return;
                  }
                  e.preventDefault();
                  const val = nullIfInput.trim();
                  const current = configForm.getValues("null_if") ?? [];
                  if (!current.includes(val)) {
                    configForm.setValue("null_if", [...current, val], { shouldDirty: true });
                  }
                  setNullIfInput("");
                }}
              />
              {(configForm.watch("null_if") ?? []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {(configForm.watch("null_if") ?? []).map((val: string, i: number) => (
                    <Badge key={i} variant="secondary" className="gap-1 pl-2 pr-1">
                      {val === "" ? '""' : val}
                      <button
                        type="button"
                        onClick={() => {
                          const current = configForm.getValues("null_if") ?? [];
                          configForm.setValue(
                            "null_if",
                            current.filter((_: string, idx: number) => idx !== i),
                            { shouldDirty: true }
                          );
                        }}
                        className="ml-0.5 rounded-full opacity-60 hover:opacity-100"
                        aria-label={`Remove ${val}`}
                      >
                        <X className="size-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Encoding</Label>
              <Select
                value={configForm.watch("encoding") ?? "UTF8"}
                onValueChange={(v) => configForm.setValue("encoding", v, { shouldDirty: true })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select encoding" />
                </SelectTrigger>
                <SelectContent className="z-[1300]">
                  <SelectItem value="UTF8">UTF-8</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFileConfigOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!configForm.formState.isDirty}
              onClick={() => {
                const vals = configForm.getValues();
                form.setValue("metadata_platform_storage_key", vals.metadata_platform_storage_key);
                if (selectedFileMetadata) {
                  form.setValue("metadata_config", selectedFileMetadata);
                }
                form.setValue("metadata_config.file_format.delimiter", vals.delimiter);
                form.setValue("metadata_config.file_format.encoding", vals.encoding);
                form.setValue("metadata_config.file_format.null_if", vals.null_if);
                setFileConfigOpen(false);
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace existing table?</AlertDialogTitle>
            <AlertDialogDescription>
              The table{" "}
              <strong>
                {schema}.{tableName}
              </strong>{" "}
              already exists. Proceeding will permanently replace all of its current data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmOpen(false);
                trackEvent("dataset_table_overwrite_confirmed", {
                  dataset_id: datasetId,
                  table_name: tableName,
                  database_schema: schema,
                });
                form.handleSubmit(doSubmit)();
              }}
            >
              Replace Table
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormProvider>
  );
};
